/**
 * Création, modification et suivi d'un rendez-vous.
 * Un seul composant pour tout le cycle de vie : c'est le geste le plus répété du métier,
 * il ne doit jamais demander de réfléchir à « où est le bouton ».
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Check, CircleSlash, Clock, CreditCard, Trash2, UserPlus, X } from "lucide-react";
import { api, messageErreur } from "../api";
import {
  Btn, Champ, Choix, Feuille, Micro, Pastille, ScoreRisque, Zone,
} from "./Bits";
import {
  STATUTS, duree, euros, heure, isoJour, jourLong, telephoneLisible,
} from "../lib/format";

export function RdvFeuille({ ouvert, contexte, surFermeture, surEnregistrement }) {
  const rdvExistant = contexte?.rdv || null;
  const [prestations, setPrestations] = useState([]);
  const [clients, setClients] = useState([]);
  const [chargeInit, setChargeInit] = useState(true);

  const [nouveauClient, setNouveauClient] = useState(false);
  const [clientId, setClientId] = useState("");
  const [prenom, setPrenom] = useState("");
  const [nomFamille, setNomFamille] = useState("");
  const [tel, setTel] = useState("");
  const [mail, setMail] = useState("");

  const [serviceId, setServiceId] = useState("");
  const [jour, setJour] = useState(isoJour());
  const [creneaux, setCreneaux] = useState([]);
  const [creneau, setCreneau] = useState("");
  const [chargeCreneaux, setChargeCreneaux] = useState(false);
  const [prix, setPrix] = useState("");
  const [notes, setNotes] = useState("");
  const [envoi, setEnvoi] = useState(false);
  const [risque, setRisque] = useState(null);
  const [moyen, setMoyen] = useState("carte");
  const [pourboire, setPourboire] = useState("");
  const [modeEncaissement, setModeEncaissement] = useState(false);

  /* --------------------------------------------------------- chargement */
  useEffect(() => {
    if (!ouvert) return;
    let vivant = true;
    setChargeInit(true);
    (async () => {
      try {
        const [p, c] = await Promise.all([
          api.get("/prestations"),
          api.get("/clients", { params: { tri: "alpha" } }),
        ]);
        if (!vivant) return;
        setPrestations(p.data.filter((x) => x.actif));
        setClients(c.data);
      } catch (e) {
        toast.error(messageErreur(e, "Impossible de charger tes prestations."));
      } finally {
        if (vivant) setChargeInit(false);
      }
    })();
    return () => {
      vivant = false;
    };
  }, [ouvert]);

  useEffect(() => {
    if (!ouvert) return;
    setModeEncaissement(false);
    setRisque(null);
    if (rdvExistant) {
      setClientId(rdvExistant.client_id);
      setServiceId(rdvExistant.service_id);
      setJour(rdvExistant.jour || isoJour(new Date(rdvExistant.start_at)));
      setCreneau(rdvExistant.start_at);
      setPrix(String(rdvExistant.price ?? ""));
      setNotes(rdvExistant.notes || "");
      setNouveauClient(false);
      setPourboire(rdvExistant.tip ? String(rdvExistant.tip) : "");
      setMoyen(rdvExistant.payment_method || "carte");
      api
        .get(`/rendez-vous/${rdvExistant.id}/risque`)
        .then((r) => setRisque(r.data))
        .catch(() => {});
    } else {
      setClientId(contexte?.client_id || "");
      setNouveauClient(false);
      setPrenom("");
      setNomFamille("");
      setTel("");
      setMail("");
      setServiceId("");
      setJour(contexte?.jour || isoJour());
      setCreneau(contexte?.iso || "");
      setPrix("");
      setNotes("");
    }
  }, [ouvert, rdvExistant, contexte]);

  const chargerCreneaux = useCallback(async () => {
    if (!serviceId || !jour) {
      setCreneaux([]);
      return;
    }
    setChargeCreneaux(true);
    try {
      const { data } = await api.get("/creneaux", {
        params: { jour, service_id: serviceId, rdv_id: rdvExistant?.id },
      });
      setCreneaux(data.creneaux);
      if (!data.creneaux.some((c) => c.iso === creneau)) {
        if (rdvExistant && rdvExistant.jour === jour) setCreneau(rdvExistant.start_at);
        else setCreneau("");
      }
    } catch (e) {
      toast.error(messageErreur(e, "Impossible de lire tes disponibilités."));
    } finally {
      setChargeCreneaux(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceId, jour, rdvExistant?.id]);

  useEffect(() => {
    if (ouvert) chargerCreneaux();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ouvert, serviceId, jour]);

  const presta = useMemo(
    () => prestations.find((p) => p.id === serviceId),
    [prestations, serviceId]
  );

  useEffect(() => {
    if (presta && !rdvExistant) setPrix(String(presta.prix));
  }, [presta, rdvExistant]);

  /* ------------------------------------------------------------ actions */
  const enregistrer = async () => {
    if (!serviceId) return toast.error("Choisis une prestation.");
    if (!creneau) return toast.error("Choisis un créneau.");
    if (!rdvExistant && !nouveauClient && !clientId)
      return toast.error("Indiquez de qui il s'agit.");
    if (!rdvExistant && nouveauClient && !prenom.trim())
      return toast.error("Le prénom est nécessaire.");

    setEnvoi(true);
    try {
      if (rdvExistant) {
        await api.patch(`/rendez-vous/${rdvExistant.id}`, {
          start_at: creneau,
          service_id: serviceId,
          prix: prix === "" ? undefined : Number(prix),
          notes,
        });
        toast.success("Rendez-vous mis à jour.");
      } else {
        const corps = {
          service_id: serviceId,
          start_at: creneau,
          notes,
          prix: prix === "" ? undefined : Number(prix),
        };
        if (nouveauClient)
          corps.client_nouveau = {
            prenom: prenom.trim(),
            nom: nomFamille.trim() || null,
            phone: tel.trim() || null,
            email: mail.trim() || null,
          };
        else corps.client_id = clientId;
        await api.post("/rendez-vous", corps);
        toast.success("C'est dans l'agenda.");
      }
      surEnregistrement();
    } catch (e) {
      toast.error(messageErreur(e, "Enregistrement impossible."));
    } finally {
      setEnvoi(false);
    }
  };

  const changerStatut = async (status, message) => {
    setEnvoi(true);
    try {
      await api.patch(`/rendez-vous/${rdvExistant.id}`, { status });
      toast.success(message);
      surEnregistrement();
    } catch (e) {
      toast.error(messageErreur(e));
    } finally {
      setEnvoi(false);
    }
  };

  const encaisser = async () => {
    setEnvoi(true);
    try {
      await api.post(`/rendez-vous/${rdvExistant.id}/encaisser`, {
        payment_method: moyen,
        montant: prix === "" ? undefined : Number(prix),
        tip: pourboire === "" ? 0 : Number(pourboire),
      });
      toast.success("Encaissement enregistré.");
      surEnregistrement();
    } catch (e) {
      toast.error(messageErreur(e));
    } finally {
      setEnvoi(false);
    }
  };

  const supprimer = async () => {
    setEnvoi(true);
    try {
      await api.delete(`/rendez-vous/${rdvExistant.id}`);
      toast.success("Rendez-vous supprimé.");
      surEnregistrement();
    } catch (e) {
      toast.error(messageErreur(e));
    } finally {
      setEnvoi(false);
    }
  };

  /* --------------------------------------------------------------- vues */
  const titre = rdvExistant
    ? `${rdvExistant.client_prenom || "Rendez-vous"}${rdvExistant.client_nom ? " " + rdvExistant.client_nom : ""}`
    : "Nouveau rendez-vous";

  const pied = rdvExistant ? (
    modeEncaissement ? (
      <>
        <Btn variante="nu" onClick={() => setModeEncaissement(false)} data-testid="btn-retour">
          Retour
        </Btn>
        <Btn onClick={encaisser} charge={envoi} data-testid="btn-valider-encaissement">
          Encaisser {prix ? euros(Number(prix) + Number(pourboire || 0)) : ""}
        </Btn>
      </>
    ) : (
      <>
        <Btn variante="danger" onClick={supprimer} charge={envoi} icone={Trash2} data-testid="btn-supprimer-rdv">
          Supprimer
        </Btn>
        <Btn onClick={enregistrer} charge={envoi} data-testid="btn-enregistrer-rdv">
          Enregistrer
        </Btn>
      </>
    )
  ) : (
    <>
      <Btn variante="nu" onClick={surFermeture} data-testid="btn-annuler">
        Annuler
      </Btn>
      <Btn onClick={enregistrer} charge={envoi} data-testid="btn-creer-rdv">
        Ajouter à l'agenda
      </Btn>
    </>
  );

  return (
    <Feuille
      ouvert={ouvert}
      surFermeture={surFermeture}
      micro={rdvExistant ? `${jourLong(rdvExistant.jour)} · ${rdvExistant.heure}` : "Agenda"}
      titre={titre}
      pied={pied}
      testid="feuille-rdv"
    >
      {chargeInit ? (
        <div className="py-8 text-center text-[14px] text-[var(--ink-muted)]">Chargement…</div>
      ) : prestations.length === 0 ? (
        <div className="py-6">
          <p className="text-[15px]">
            Tu n'as pas encore de prestation. Ajoute-en une dans Réglages › Prestations pour
            pouvoir prendre des rendez-vous.
          </p>
        </div>
      ) : modeEncaissement ? (
        <div className="space-y-5">
          <div>
            <Micro className="mb-2">Moyen de paiement</Micro>
            <div className="grid grid-cols-2 gap-2">
              {[
                { c: "carte", l: "Carte" },
                { c: "especes", l: "Espèces" },
                { c: "virement", l: "Virement" },
                { c: "autre", l: "Autre" },
              ].map((m) => (
                <button
                  key={m.c}
                  onClick={() => setMoyen(m.c)}
                  data-testid={`moyen-${m.c}`}
                  className={`h-11 rounded-[4px] border text-[14px] font-semibold ${
                    moyen === m.c
                      ? "border-[var(--accent)] bg-[var(--accent-tint)] text-[var(--ink)]"
                      : "border-[var(--rule)] bg-[var(--surface)] text-[var(--ink-soft)]"
                  }`}
                >
                  {m.l}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Champ
              label="Montant"
              type="number"
              inputMode="decimal"
              value={prix}
              onChange={(e) => setPrix(e.target.value)}
              suffixe="€"
              data-testid="champ-montant"
            />
            <Champ
              label="Pourboire"
              type="number"
              inputMode="decimal"
              value={pourboire}
              onChange={(e) => setPourboire(e.target.value)}
              suffixe="€"
              placeholder="0"
              data-testid="champ-pourboire"
            />
          </div>
        </div>
      ) : (
        <div className="space-y-5">
          {/* --------------------------------------------- suivi du rendez-vous */}
          {rdvExistant && (
            <div className="surface rounded-[4px] p-4">
              <div className="flex items-center justify-between gap-3 mb-3">
                <Micro>Suivi</Micro>
                <div className="flex items-center gap-1.5">
                  <Pastille ton={STATUTS[rdvExistant.status]?.ton}>
                    {STATUTS[rdvExistant.status]?.label}
                  </Pastille>
                  {rdvExistant.paid && <Pastille ton="olive">Payé</Pastille>}
                </div>
              </div>
              {["annule", "annule_tardif"].includes(rdvExistant.status) &&
                rdvExistant.annule_par === "client" && (
                  <div
                    className="rounded-[4px] p-3 mb-3 flex items-start gap-2"
                    style={{ background: "rgba(158,65,50,0.08)", border: "1px solid var(--danger)" }}
                    data-testid="alerte-annulation-client"
                  >
                    <X size={15} strokeWidth={2.4} style={{ color: "var(--danger)", marginTop: 1 }} />
                    <p className="text-[13.5px]" style={{ color: "var(--danger)" }}>
                      La cliente a annulé elle-même ce rendez-vous
                      {rdvExistant.status === "annule_tardif" ? ", tardivement" : ""}.
                    </p>
                  </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <Btn
                  variante="fantome"
                  taille="sm"
                  icone={Check}
                  onClick={() => changerStatut("arrive", "Arrivée notée.")}
                  data-testid="btn-statut-arrive"
                >
                  Arrivée
                </Btn>
                <Btn
                  variante="fantome"
                  taille="sm"
                  icone={CreditCard}
                  onClick={() => setModeEncaissement(true)}
                  data-testid="btn-ouvrir-encaissement"
                >
                  Encaisser
                </Btn>
                <Btn
                  variante="fantome"
                  taille="sm"
                  icone={Clock}
                  onClick={() => changerStatut("confirme", "Rendez-vous confirmé.")}
                  data-testid="btn-statut-confirme"
                >
                  Confirmé
                </Btn>
                <Btn
                  variante="danger"
                  taille="sm"
                  icone={CircleSlash}
                  onClick={() => changerStatut("absent", "Absence enregistrée.")}
                  data-testid="btn-statut-absent"
                >
                  Absence
                </Btn>
              </div>
              {rdvExistant.status !== "annule" && rdvExistant.status !== "annule_tardif" && (
                <Btn
                  variante="nu"
                  taille="sm"
                  className="mt-2 w-full"
                  onClick={() => changerStatut("annule", "Rendez-vous annulé.")}
                  data-testid="btn-statut-annule"
                >
                  Annuler ce rendez-vous
                </Btn>
              )}
            </div>
          )}

          {/* ------------------------------------------------------- le client */}
          {!rdvExistant && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <Micro>Qui</Micro>
                <button
                  onClick={() => setNouveauClient((v) => !v)}
                  data-testid="btn-basculer-nouveau-client"
                  className="text-[12px] font-semibold text-[var(--accent)] inline-flex items-center gap-1"
                >
                  <UserPlus size={13} strokeWidth={1.8} />
                  {nouveauClient ? "Choisir dans mes clientes" : "Nouvelle personne"}
                </button>
              </div>
              {nouveauClient ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <Champ
                      label="Prénom"
                      value={prenom}
                      onChange={(e) => setPrenom(e.target.value)}
                      placeholder="Inès"
                      data-testid="champ-prenom"
                    />
                    <Champ
                      label="Nom"
                      value={nomFamille}
                      onChange={(e) => setNomFamille(e.target.value)}
                      placeholder="Bernard"
                      data-testid="champ-nom"
                    />
                  </div>
                  <Champ
                    label="Mobile"
                    value={tel}
                    onChange={(e) => setTel(e.target.value)}
                    placeholder="06 12 34 56 78"
                    inputMode="tel"
                    indice="Utile pour la joindre directement en cas de besoin"
                    data-testid="champ-tel"
                  />
                  <Champ
                    label="E-mail"
                    value={mail}
                    onChange={(e) => setMail(e.target.value)}
                    placeholder="ines@exemple.fr"
                    inputMode="email"
                    data-testid="champ-email"
                  />
                </div>
              ) : (
                <Choix
                  valeur={clientId}
                  surChangement={setClientId}
                  placeholder={clients.length ? "Chercher un client…" : "Aucun client enregistré"}
                  testid="choix-client"
                  options={clients.map((c) => ({
                    valeur: c.id,
                    label: `${c.nom_affiche}${c.phone ? " · " + telephoneLisible(c.phone) : ""}`,
                  }))}
                />
              )}
            </div>
          )}

          {/* ------------------------------------------------------ prestation */}
          <div className="space-y-3">
            <Micro>Prestation</Micro>
            <Choix
              valeur={serviceId}
              surChangement={setServiceId}
              placeholder="Choisir une prestation"
              testid="choix-prestation"
              options={prestations.map((p) => ({
                valeur: p.id,
                label: `${p.nom} · ${duree(p.duree_min)} · ${euros(p.prix)}`,
              }))}
            />
            <div className="grid grid-cols-2 gap-3">
              <Champ
                label="Jour"
                type="date"
                value={jour}
                onChange={(e) => setJour(e.target.value)}
                data-testid="champ-jour"
              />
              <Champ
                label="Prix"
                type="number"
                inputMode="decimal"
                value={prix}
                onChange={(e) => setPrix(e.target.value)}
                suffixe="€"
                data-testid="champ-prix"
              />
            </div>
          </div>

          {/* -------------------------------------------------------- créneaux */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Micro>Créneau</Micro>
              {presta && (
                <span className="text-[12px] text-[var(--ink-muted)]">
                  {duree(presta.duree_min)}
                  {presta.buffer_min ? ` + ${presta.buffer_min} min de battement` : ""}
                </span>
              )}
            </div>
            {!serviceId ? (
              <p className="text-[13px] text-[var(--ink-muted)]">
                Choisis d'abord une prestation pour voir tes disponibilités.
              </p>
            ) : chargeCreneaux ? (
              <div className="grid grid-cols-4 gap-2">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="h-11 rounded-[4px] bg-[var(--rule)] opacity-40 animate-pulse" />
                ))}
              </div>
            ) : creneaux.length === 0 ? (
              <p className="text-[13px] text-[var(--ink-soft)]">
                Aucune place ce jour-là. Essaie une autre date, ou vérifie tes horaires dans
                Réglages.
              </p>
            ) : (
              <div className="grid grid-cols-4 gap-2 max-h-[188px] overflow-y-auto no-scrollbar">
                {creneaux.map((c) => (
                  <button
                    key={c.iso}
                    onClick={() => setCreneau(c.iso)}
                    data-testid={`creneau-${c.heure}`}
                    className={`h-11 rounded-[4px] border text-[14px] num ${
                      creneau === c.iso
                        ? "border-[var(--accent)] bg-[var(--accent-tint)] text-[var(--ink)]"
                        : "border-[var(--rule)] bg-[var(--surface)] text-[var(--ink-soft)] hover:border-[var(--rule-strong)]"
                    }`}
                    style={{ transition: "border-color var(--dur) var(--ease)" }}
                  >
                    {c.heure}
                  </button>
                ))}
              </div>
            )}
          </div>

          <Zone
            label="Notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Formule, allergie, préférence… ce que tu dois te rappeler."
            data-testid="champ-notes"
          />

          {risque && risque.niveau !== "faible" && <ScoreRisque risque={risque} />}
        </div>
      )}
    </Feuille>
  );
}
